import cs from "classnames";
import { append, equals, includes, reject, uniq } from "ramda";
import React from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";

import { Checkbox } from "src/common/components/Checkbox";
import { useOnClickOutside } from "src/common/hooks/useOnClickOutside";
import { normalizeSearchString } from "src/common/utils/common";
import { scrollIntoView } from "src/common/utils/dom";
import { genericMessages } from "src/messages/generic";

import "./MultiSelect.css";

interface MultiSelectProps<T> {
	disabled?: boolean;
	options: Option<T>[];
	value: T[];
	onChangeValue(value: T[]): unknown;
}

export interface Option<T = string> {
	value: T;
	label: string;
}

export function MultiSelect<T = string>({
	disabled,
	options,
	value,
	onChangeValue
}: MultiSelectProps<T>): React.ReactElement {
	const { formatMessage } = useIntl();

	const containerRef = React.useRef<HTMLDivElement>(null);
	const queryRef = React.useRef<HTMLInputElement>(null);

	const [focused, setFocused] = React.useState(false);
	const [query, setQuery] = React.useState("");
	const [index, setIndex] = React.useState(0);

	const filteredOptions = React.useMemo(
		() => filterOptions(options, query),
		[options, query]
	);

	const selectedLabels = React.useMemo(
		() =>
			value.map(value => {
				const index = options.findIndex(option =>
					equals(option.value, value)
				);
				return index === -1 ? "" : options[index].label;
			}),
		[options, value]
	);

	const optionsRef = React.useRef(options);
	React.useEffect(() => {
		optionsRef.current = options;
	}, [options]);

	const valueRef = React.useRef(value);
	React.useEffect(() => {
		valueRef.current = value;
	}, [value]);

	const indexRef = React.useRef(index);
	React.useEffect(() => {
		indexRef.current = index;
	}, [index]);

	const filteredOptionsRef = React.useRef(filteredOptions);
	React.useEffect(() => {
		filteredOptionsRef.current = filteredOptions;
	}, [filteredOptions]);

	const onFocus = React.useCallback((): void => {
		setFocused(true);
	}, []);

	const onBlur = React.useCallback(() => {
		setFocused(false);
		setQuery("");
		setIndex(0);
	}, []);

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent): void => {
			if (event.key === "ArrowDown") {
				setIndex(index =>
					Math.min(index + 1, optionsRef.current.length - 1)
				);
			}
			if (event.key === "ArrowUp") {
				setIndex(index => Math.max(index - 1, 0));
			}
			if (event.key === "Enter") {
				onChangeValue(
					toggleValue(
						valueRef.current,
						filteredOptionsRef.current[indexRef.current].value
					)
				);
			}
			if (event.key === "Escape") {
				onBlur();
			}
		},
		[onChangeValue, onBlur]
	);

	const onChangeQuery = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			setQuery(event.target.value);
			setIndex(0);
		},
		[]
	);

	useOnClickOutside(containerRef, onBlur);
	useFocusRef(queryRef, focused);

	return (
		<div
			ref={containerRef}
			className={cs("multi-select", {
				"multi-select--disabled": disabled,
				"multi-select--focused": focused
			})}
			tabIndex={disabled ? -1 : 0}
			onFocus={onFocus}
			onKeyDown={onKeyDown}
		>
			<div className="multi-select__value">
				{value.length === 0 ? (
					<FormattedMessage {...genericMessages.pleaseSelect} />
				) : (
					selectedLabels.join(", ")
				)}
			</div>
			<div className="multi-select__actions">
				<button
					className="multi-select__button multi-select__clear"
					onClick={() => {
						onChangeValue([]);
					}}
					disabled={disabled || value.length === 0}
				>
					<FaTimes />
				</button>
				<div className="multi-select__button multi-select__chevron">
					<FaChevronDown />
				</div>
			</div>
			<div className="multi-select__menu">
				<input
					ref={queryRef}
					className="multi-select__query"
					placeholder={formatMessage(genericMessages.search)}
					value={query}
					onChange={onChangeQuery}
				/>
				<div className="multi-select__option-list">
					{filteredOptions.length === 0 ? (
						<div className="multi-select__no-option">
							<FormattedMessage {...genericMessages.noMatches} />
						</div>
					) : (
						filteredOptions.map((option, i) => (
							<MultiSelectOption
								key={option.label}
								selected={index === i}
								checked={value.some(equals(option.value))}
								onChangeChecked={() => {
									onChangeValue(
										toggleValue(value, option.value)
									);
									setIndex(i);
								}}
							>
								{option.label}
							</MultiSelectOption>
						))
					)}
				</div>
			</div>
		</div>
	);
}

interface MultiSelectOptionProps {
	selected: boolean;
	checked: boolean;
	onChangeChecked(checked: boolean): unknown;
}

const MultiSelectOption: React.FunctionComponent<
	React.PropsWithChildren<MultiSelectOptionProps>
> = ({ selected, checked, onChangeChecked, children }) => {
	const ref = React.useRef<HTMLDivElement>(null);
	React.useEffect(() => {
		if (
			!selected ||
			ref.current === null ||
			ref.current.scrollIntoView === undefined
		) {
			return;
		}
		scrollIntoView(ref.current);
	}, [selected]);
	return (
		<div
			ref={ref}
			className={cs("multi-select__option", {
				"multi-select__option--selected": selected
			})}
		>
			<Checkbox checked={checked} onChangeChecked={onChangeChecked}>
				{children}
			</Checkbox>
		</div>
	);
};

function useFocusRef(
	ref: React.RefObject<HTMLElement | null>,
	focused: boolean
): void {
	React.useEffect(() => {
		if (ref.current === null || !focused) {
			return;
		}
		ref.current.focus();
	}, [ref, focused]);
}

function filterOptions<T>(options: Option<T>[], query: string): Option<T>[] {
	const normalizedQuery = normalizeSearchString(query);
	return options.filter(option =>
		normalizeSearchString(option.label).includes(normalizedQuery)
	);
}

function toggleValue<T>(a: T[], v: T): T[] {
	return includes(v, a) ? removeValue(a, v) : addValue(a, v);
}

function addValue<T>(a: T[], v: T): T[] {
	return uniq(append(v, a));
}

function removeValue<T>(a: T[], v: T): T[] {
	return reject(equals(v), a);
}
